import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { uniq } from 'lodash';
import { observer } from 'mobx-react-lite';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import CloseIcon from '../../svgs/CloseIcon';
import PlusIcon from '../../svgs/PlusIcon';
import ColorPicker, { ColorTabs } from '../common/ColorPicker';
import Accordion from './Accordion';
import AddTile from './AddTile';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

type Props = {
  type: 'primary' | 'secondary';
};

type PickerState =
  | { mode: 'add'; anchor: DOMRect }
  | { mode: 'edit'; index: number; anchor: DOMRect };

const BrandKitColors = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const { type } = props;

  const branding = videoCreator.organization?.branding;
  const colors =
    type === 'primary' ? branding?.primaryColors : branding?.secondaryColors;
  const [value, setValue] = useState('');

  const handleSave = async (newValue: string) => {
    const organization = videoCreator.organization;
    organization!.branding = {
      ...organization!.branding!,
      ...(type === 'primary'
        ? { primaryColors: newValue }
        : { secondaryColors: newValue }),
    };
    await datoClientStore.albumRepository?.update(organization!);
  };

  useEffect(() => {
    if (colors) setValue(colors);
  }, [Boolean(colors)]);
  const colorList = value ? value.split(',') : [];

  const [picker, setPicker] = useState<PickerState | null>(null);
  // Last color the picker committed via getValue: undefined = untouched,
  // null = erased with the picker's trash button, string = rgba color.
  const pendingColorRef = useRef<string | null | undefined>(undefined);

  const openPicker = (
    e: MouseEvent<HTMLElement>,
    state: { mode: 'add' } | { mode: 'edit'; index: number },
  ) => {
    pendingColorRef.current = undefined;
    setPicker({ ...state, anchor: e.currentTarget.getBoundingClientRect() });
  };

  const applyColors = (updated: string[]) => {
    const newValue = uniq(updated.map(c => c.toUpperCase())).join(',');
    if (newValue === value) return;
    setValue(newValue);
    handleSave(newValue);
  };

  const handlePickerClose = () => {
    const active = picker;
    const pending = pendingColorRef.current;
    pendingColorRef.current = undefined;
    setPicker(null);
    if (!active || pending === undefined) return;
    if (pending === null) {
      // Trash button in the picker: confirm removal when editing an existing
      // color; when adding, there is nothing to remove yet — just dismiss.
      if (active.mode === 'edit') setPendingDeleteIndex(active.index);
      return;
    }
    const hex = videoCreator.textBrandingService.rgbaToHex(pending).slice(0, 7);
    if (!hex) return;
    if (active.mode === 'add') {
      applyColors([...colorList, hex]);
    } else {
      const updated = [...colorList];
      updated[active.index] = hex;
      applyColors(updated);
    }
  };

  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null,
  );
  const handleRemoveColor = (index: number) => {
    const newValue = colorList.filter((_, i) => i !== index).join(',');
    setValue(newValue);
    handleSave(newValue);
  };

  return (
    <Accordion
      id={type === 'primary' ? 'primary-colors' : 'secondary-colors'}
      title={`${type.charAt(0).toUpperCase() + type.slice(1)} Colors${
        colors ? ` (${colorList.length})` : ''
      }`}
    >
      {branding !== undefined ? (
        <Container showEmptyMessage={!colorList.length && !picker}>
          {colorList.length ? (
            <ColorList>
              {colorList.map((color, index) => (
                <ColorItem key={color} color={color}>
                  <div
                    role="button"
                    className="color-tile"
                    onClick={e => openPicker(e, { mode: 'edit', index })}
                  >
                    <div />
                    <span>{color}</span>
                    <div
                      role="button"
                      className="delete-color-icon"
                      onClick={e => {
                        e.stopPropagation();
                        setPendingDeleteIndex(index);
                      }}
                    >
                      <CloseIcon
                        width="9"
                        height="9"
                        strokeColor="var(--accent)"
                      />
                    </div>
                  </div>
                </ColorItem>
              ))}
              <AddTile
                className="add-tile"
                onClick={e => openPicker(e, { mode: 'add' })}
              />
            </ColorList>
          ) : (
            <EmptyMessage>
              <div
                className="empty-content"
                role="button"
                onClick={e => openPicker(e, { mode: 'add' })}
              >
                <PlusIcon width="40" strokeColor="var(--text-secondary)" />
                <span>Add your first color</span>
              </div>
            </EmptyMessage>
          )}
        </Container>
      ) : null}

      {picker ? (
        <ColorPicker
          pos={picker.anchor}
          tab={ColorTabs.custom}
          solidDefaultAlpha
          defaultValue={picker.mode === 'edit' ? colorList[picker.index] : null}
          getValue={color => {
            pendingColorRef.current = color;
          }}
          onClose={handlePickerClose}
        />
      ) : null}

      {pendingDeleteIndex !== null && colorList[pendingDeleteIndex] && (
        <ConfirmDeleteDialog
          title={`Delete "${colorList[pendingDeleteIndex]}"?`}
          onCancel={() => setPendingDeleteIndex(null)}
          onConfirm={() => {
            // Close immediately for one-click responsiveness; the removal
            // updates local state synchronously and persists in the
            // background.
            const index = pendingDeleteIndex;
            setPendingDeleteIndex(null);
            handleRemoveColor(index);
          }}
        >
          <p>
            This color will be removed from{' '}
            {type === 'primary' ? 'Primary' : 'Secondary'} Colors.
          </p>
        </ConfirmDeleteDialog>
      )}
    </Accordion>
  );
});

const Container = styled.div<{ showEmptyMessage: boolean }>`
  border-radius: 8px;
  background: var(--surface-elevated);
  padding: ${({ showEmptyMessage }) => (showEmptyMessage ? '0' : '24px')};
  cursor: ${({ showEmptyMessage }) =>
    showEmptyMessage ? 'pointer' : 'default'};
  min-height: 120px;
  position: relative;
  transition: var(--transition-normal);
  ${({ showEmptyMessage }) =>
    showEmptyMessage &&
    css`
      &:hover {
        background: var(--border);
      }
    `}
`;

const ColorList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
  gap: 24px;
`;

const ColorItem = styled.div<{ color: string }>`
  position: relative;
  > .color-tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    position: relative;
    cursor: pointer;
    &:hover {
      > span {
        color: var(--text-secondary);
      }
      > .delete-color-icon {
        opacity: 1;
        cursor: pointer;
        transition-delay: 0.4s;
      }
    }
    > div {
      background: ${({ color }) => color};
      width: 72px;
      height: 72px;
      border-radius: 4px;
    }
    > span {
      color: var(--text-muted);
      text-align: center;
      font-size: var(--text-sm);
      font-weight: var(--font-weight-regular);
      transition: var(--transition-normal);
    }
    > .delete-color-icon {
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-color: var(--border);
      position: absolute;
      top: -8px;
      right: -8px;
      opacity: 0;
      cursor: none;
      transition: var(--transition-normal);
      svg path {
        transition: var(--transition-normal);
      }
      &:hover {
        svg path {
          stroke: var(--destructive);
        }
      }
    }
  }
`;

const EmptyMessage = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  > .empty-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    > span {
      color: var(--text-secondary);
      text-align: center;
      font-size: 16px;
      font-weight: var(--font-weight-regular);
    }
  }
`;

export default BrandKitColors;
